import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useBranchList, type BranchWithStats } from "../../api/branches";
import {
  fetchScheduleSettings,
  type ScheduleSettingsResponse,
} from "../../api/schedulingSettings";
import { useAuth } from "../../auth/AuthContext";
import { UI_LOADING } from "../../constants/uiLabels";
import "./ScheduleSettingsSummary.css";

type BranchSummary = {
  branch: BranchWithStats;
  data: ScheduleSettingsResponse | null;
  error: string | null;
};

function timeLabel(value: string | undefined): string {
  if (!value) return "—";
  return value.length >= 5 ? value.slice(0, 5) : value;
}

function formatEffectiveFrom(value: string | undefined): string {
  if (!value) return "—";
  const iso = value.slice(0, 10);
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}.${m}.${y}`;
}

export function ScheduleSettingsSummary() {
  const { session } = useAuth();
  const isTenantAdmin = session?.user?.role === "tenant_admin";
  const managerBranchId = session?.user?.branch_id ?? null;

  const branchList = useBranchList({ isActive: true });
  const branches = useMemo(() => {
    const all = branchList.data ?? [];
    if (isTenantAdmin) return all;
    if (managerBranchId) return all.filter((b) => b.id === managerBranchId);
    return all;
  }, [branchList.data, isTenantAdmin, managerBranchId]);

  const [summaries, setSummaries] = useState<BranchSummary[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (branches.length === 0) {
      setSummaries([]);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    Promise.all(
      branches.map(async (branch) => {
        try {
          const data = await fetchScheduleSettings(branch.id);
          return { branch, data, error: null as string | null };
        } catch {
          return { branch, data: null, error: "Şablon Yüklenemedi." };
        }
      }),
    )
      .then(setSummaries)
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [branches]);

  if (branchList.loading || loading) {
    return <div className="kp-sched-summary__loading">Şablon Özetleri {UI_LOADING}</div>;
  }

  if (branchList.error) {
    return <div className="kp-sched-summary__error">{branchList.error}</div>;
  }

  if (branches.length === 0) {
    return <div className="kp-sched-summary__empty">Aktif Şube Bulunamadı.</div>;
  }

  return (
    <div className="kp-sched-summary">
      <p className="kp-sched-summary__intro">
        Aşağıda {isTenantAdmin ? "kurumunuzdaki her sube icin" : "subeniz icin"}{" "}
        <strong>bugune gore</strong> gecerli slot şablonu ozeti yer alir. Kendi subenizi
        duzenlemek icin{" "}
        <Link to="/calendar">Takvim</Link> → <strong>Rezervasyon Ayarları</strong> kullanin.
      </p>

      <div className="kp-sched-summary__grid">
        {summaries.map(({ branch, data, error }) => (
          <article key={branch.id} className="kp-sched-summary__card">
            <header className="kp-sched-summary__card-head">
              <h3>{branch.name}</h3>
              <Link to="/calendar" className="kp-btn kp-btn--small">
                Takvimde Düzenle
              </Link>
            </header>

            {error && <p className="kp-sched-summary__error">{error}</p>}

            {data && (
              <>
                <dl className="kp-sched-summary__dl">
                  <div>
                    <dt>Geçerlilik</dt>
                    <dd>{formatEffectiveFrom(String(data.settings.effective_from))}</dd>
                  </div>
                  <div>
                    <dt>Gün Başlangıcı</dt>
                    <dd>{timeLabel(data.settings.day_start_time)}</dd>
                  </div>
                  <div>
                    <dt>Gunluk ders</dt>
                    <dd>{data.settings.daily_lesson_count}</dd>
                  </div>
                  <div>
                    <dt>Ders Süresi</dt>
                    <dd>{data.settings.lesson_duration_minutes} dk</dd>
                  </div>
                  <div>
                    <dt>Dinlenme</dt>
                    <dd>
                      {data.settings.break_duration_minutes} dk
                      {Number(data.settings.break_every_n_lessons) > 1
                        ? ` · ${data.settings.break_every_n_lessons} ders sonunda`
                        : " · her ders arası"}
                    </dd>
                  </div>
                  <div>
                    <dt>Ogle</dt>
                    <dd>
                      {timeLabel(data.settings.lunch_start_time)} ·{" "}
                      {data.settings.lunch_duration_minutes} dk
                    </dd>
                  </div>
                  <div>
                    <dt>Hesaplanan Gün Sonu</dt>
                    <dd>{data.preview_meta?.computed_day_end ?? timeLabel(data.settings.day_end_time)}</dd>
                  </div>
                </dl>

                <p className="kp-sched-summary__slots-title">
                  Slotlar ({data.preview_meta?.actual_lesson_count ?? data.slots_preview.length})
                </p>
                <ul className="kp-sched-summary__slots">
                  {data.slots_preview.map((s) => (
                    <li key={s.index}>
                      {s.start}–{s.end}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </article>
        ))}
      </div>
    </div>
  );
}
